import React, {useState} from "react";
import {Button, Form, Input, message, Modal, Tooltip} from "antd";
import {HttpService} from "../../services/http/Http.service";
import {useProgressContext} from "../../services/ProgressContext";
import {SimulatorPersonIdPair} from "../../model/models";
import {EnvService} from "../../services/Env.service";
import {AuthService} from "../../services/Auth.service";
import {HttpErrorService} from "../../services/http/HttpError.service";
import CustomSpinner from "../spinner/CustomSpinner";

interface SignerProps {
    selectedSimulatorPersonPairs: SimulatorPersonIdPair[];
    onSuccess?: () => void;
    submitUrlPrefix?: string;
    buttonLabel?: string;
}

const Signer: React.FC<SignerProps> = ({selectedSimulatorPersonPairs, onSuccess, submitUrlPrefix = '/signer/async-process', buttonLabel = 'Firma'}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSigning, setIsSigning] = useState(false);
    const [uuid, setUuid] = useState<string | null>(null);
    const [form] = Form.useForm();
    const {startPolling, isPolling} = useProgressContext();

    const startSigning = () => {
        if (selectedSimulatorPersonPairs.length > 0)
            HttpService.getInstanceAxios().get(`/auth/autenticazione-firma?t=${new Date().getTime()}`)
                .then(response => {
                    const authenticated = response.data || false;
                    if (!authenticated) {
                        const windowFeatures = "left=150,top=150,width=1280,height=768";
                        console.warn("Signing access token in missing, redirecting to authentication page")
                        let openedWindow = window.open(EnvService.getBackEndUrl() + "/auth/autentica-firma",
                            "Autenticazione Sessione Operatore",
                            windowFeatures);

                        if (openedWindow != null) {
                            const timer = setInterval(() => {
                                // @ts-ignore
                                if (openedWindow.closed) {
                                    clearInterval(timer);
                                    console.debug("Window closed")
                                    asyncSigningStarter();
                                }
                            }, 500);
                        }
                    } else {
                        asyncSigningStarter();
                    }
                });
        else
            message.error("Nessuna pratica selezionata!")
    };

    const asyncSigningStarter = async () => {
        if (selectedSimulatorPersonPairs.length === 0) {
            message.error("Nessun utente selezionato.");
            return;
        }

        try {
            const response = await HttpService.getInstanceAxios().post(
                `/signer/async-process`,
                selectedSimulatorPersonPairs
            );

            message.success("Caricamento sul sistema di firma iniziato");
            setUuid(response.data);

            startPolling(response.data, () => {
                message.success("Caricamento sul sistema di firma completato");
                setIsModalOpen(true);
            });
        } catch (error) {
            HttpErrorService.handle("Errore durante il caricamento sul sistema di firma", error, true)
        }
    };

    const handleModalSubmit = async () => {
        try {
            const values = await form.validateFields();
            setIsSigning(true);

            await HttpService.getInstanceAxios().post(
                `${submitUrlPrefix}/${uuid}`,
                {
                    otp: values.otp,
                    username: values.username,
                    password: values.password,
                    simulatorPersonIdPairs: selectedSimulatorPersonPairs,
                },
                {
                    timeout: 600000,
                }
            );

            message.success("Firma digitale completata con successo.");
            setIsModalOpen(false);
            form.resetFields();
            if (onSuccess) onSuccess();
        } catch (error) {
            HttpErrorService.handle("Errore durante la firma.", error, true)
        }finally {
            setIsSigning(false);
        }
    };

    return (
        <>
            {AuthService.isAdmin() &&
                <Tooltip title={isPolling ? "Processo di firma in corso" : undefined}>
                    <Button className="custom-label-color"
                            onClick={startSigning}
                            size={"large"}
                            disabled={selectedSimulatorPersonPairs.length === 0 || isPolling}
                            type={"primary"}>
                        {buttonLabel}
                    </Button>
                </Tooltip>
            }

            <Modal
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                onOk={handleModalSubmit}
                title="Firma Digitale"
                okText="Firma"
                destroyOnClose
            >
                <Form form={form} layout="vertical" disabled={isSigning} >
                    <Form.Item
                        label="Username"
                        name="username"
                        rules={[{required: true, message: "Inserisci lo username"}]}
                    >
                        <Input/>
                    </Form.Item>
                    <Form.Item
                        label="Password"
                        name="password"
                        rules={[{required: true, message: "Inserisci la password"}]}
                    >
                        <Input.Password/>
                    </Form.Item>
                    <Form.Item
                        label="OTP"
                        name="otp"
                        rules={[{required: true, message: "Inserisci il codice OTP"}]}
                    >
                        <Input/>
                    </Form.Item>
                </Form>
                <CustomSpinner
                    message={"Firma in corso..."}
                    open={isSigning}
                />
            </Modal>
        </>
    );
};

export default Signer;
